// Loglama seviyesi
type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

// Varsayılan log seviyesi - geliştirme ortamında 'debug', üretimde 'error'
const DEFAULT_LOG_LEVEL: LogLevel = import.meta.env.DEV ? 'debug' : 'warn';

// Global loglama seviyesi
let currentLogLevel: LogLevel = DEFAULT_LOG_LEVEL;

// Loglama seviyelerinin öncelik sırası
const LOG_PRIORITIES: Record<LogLevel, number> = {
  'debug': 0,
  'info': 1,
  'warn': 2,
  'error': 3,
  'none': 4
};

// İlk yükleme loglama hızını sınırlayan fonksiyon
const throttledLogs: Record<string, boolean> = {};

/**
 * Belirtilen log seviyesini ayarlar
 * @param level Ayarlanacak log seviyesi
 */
export const setLogLevel = (level: LogLevel): void => {
  currentLogLevel = level;
  console.log(`Log seviyesi değiştirildi: ${level}`);
};

/**
 * Throttled (sınırlandırılmış) log - her key için sadece bir kez çalışır
 * @param key Benzersiz log anahtarı
 * @param message Log mesajı
 * @param level Log seviyesi
 * @returns Log gösterildiyse true, gösterilmediyse false
 */
export const logOnce = (key: string, message: string, level: LogLevel = 'info'): boolean => {
  if (throttledLogs[key]) {
    return false;
  }
  
  if (LOG_PRIORITIES[level] >= LOG_PRIORITIES[currentLogLevel]) {
    switch (level) {
      case 'debug':
        console.debug(message);
        break;
      case 'info':
        console.info(message);
        break;
      case 'warn':
        console.warn(message);
        break;
      case 'error':
        console.error(message);
        break;
    }
    
    throttledLogs[key] = true;
    return true;
  }
  
  return false;
};

/**
 * API isteği için URL ve parametrelerini loglar - sınırlandırılmış
 * @param endpoint API endpoint'i
 * @param url Tam URL
 * @param params İstek parametreleri (opsiyonel)
 */
export const logApiRequest = (endpoint: string, url: string, params?: any): void => {
  // Debug modda değilse veya log seviyesi debug değilse
  if (LOG_PRIORITIES[currentLogLevel] > LOG_PRIORITIES.debug) {
    return;
  }
  
  // Endpoint için maksimum log sayısını kontrol et
  const key = `api_${endpoint}`;
  if (!throttledLogs[key]) {
    throttledLogs[key] = true;
    console.log(`API isteği: ${endpoint}`);
    console.log(`URL: ${url}`);
    if (params) {
      console.log('Parametreler:', params);
    }
    console.log('Benzer API istekleri için loglar gösterilmeyecek');
  }
};

/**
 * API yanıtını sınırlandırılmış olarak logla
 * @param endpoint API endpoint'i
 * @param status Yanıt durumu
 * @param data Yanıt verisi
 */
export const logApiResponse = (endpoint: string, status: string, data?: any): void => {
  // Debug modda değilse veya log seviyesi debug değilse
  if (LOG_PRIORITIES[currentLogLevel] > LOG_PRIORITIES.debug) {
    return;
  }
  
  // Endpoint için maksimum log sayısını kontrol et
  const key = `api_response_${endpoint}`;
  if (!throttledLogs[key]) {
    throttledLogs[key] = true;
    console.log(`API yanıtı: ${endpoint}, durum: ${status}`);
    if (data && LOG_PRIORITIES[currentLogLevel] === LOG_PRIORITIES.debug) {
      console.log('Veri:', typeof data === 'object' ? 'Nesne alındı' : data);
    }
  }
};

/**
 * Hata mesajını log et
 * @param endpoint İlgili API endpoint'i veya işlem
 * @param error Hata nesnesi veya mesajı
 */
export const logApiError = (endpoint: string, error: any): void => {
  const errorMessage = error?.message || error?.toString() || 'Bilinmeyen hata';
  console.error(`API hatası (${endpoint}): ${errorMessage}`);
  
  // Rate limit hataları için özel uyarı
  if (errorMessage.includes('rate limit') || errorMessage.includes('çok fazla istek')) {
    console.warn(`⚠️ DİKKAT: Rate limit aşıldı - "${endpoint}" endpoint'i için çok fazla istek yapılıyor!`);
  }
};

/**
 * Log kayıtlarını temizle - test veya yeni istek grupları için
 */
export const clearLogThrottling = (): void => {
  Object.keys(throttledLogs).forEach(key => {
    delete throttledLogs[key];
  });
};

/**
 * Konsol içinde görülebilir gruplandırılmış log başlığı oluşturur
 * @param title Başlık metni
 */
export const logGroupHeader = (title: string): void => {
  if (LOG_PRIORITIES[currentLogLevel] <= LOG_PRIORITIES.info) {
    console.log(`\n=== ${title} ===\n`);
  }
};

// Başlangıç log seviyesini göster
logOnce('log_level', `Log seviyesi: ${currentLogLevel}`, 'info');

// Debug modunda kullanım bilgisi
if (import.meta.env.DEV) {
  logOnce('log_usage', 'Log seviyesini değiştirmek için konsola "vagonlaApp.setLogLevel(\'debug|info|warn|error|none\')" yazabilirsiniz', 'info');
  
  // Konsol üzerinden erişim için global nesne
  (window as any).vagonlaApp = {
    setLogLevel,
    logLevel: () => currentLogLevel
  };
}
